import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import {
  Button, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle,
} from '@mui/material';

/**
 * App-wide "you have unsaved changes" guard. A page registers its dirty
 * state (and how to save/discard it) via useUnsavedChangesGuard(); anything
 * that wants to navigate away — AppLayout's nav links, a page's own Cancel
 * button, or the browser itself (refresh/close tab) — goes through
 * confirmNavigation()/the beforeunload listener instead of navigating
 * directly, so the same Save/Discard/Cancel prompt appears everywhere
 * instead of each editable page building its own.
 */
const UnsavedChangesContext = createContext(null);
let _nextRegistrationId = 1;

export function UnsavedChangesProvider({ children }) {
  // Keyed, not a single slot — multiple editable forms (e.g. several
  // accordion cards on Alarm Configuration) can be mounted and dirty at
  // the same time; a single last-write-wins ref would silently drop all
  // but the most recently mounted one's guard.
  const registryRef = useRef(new Map()); // id -> { isDirty, onSave, onDiscard }
  const [dialogOpen, setDialogOpen] = useState(false);
  const pendingActionRef = useRef(null);
  const [saving, setSaving] = useState(false);

  const anyDirty = useCallback(
    () => [...registryRef.current.values()].some((s) => s.isDirty),
    [],
  );

  const register = useCallback((id, state) => {
    registryRef.current.set(id, state);
  }, []);

  const unregister = useCallback((id) => {
    registryRef.current.delete(id);
  }, []);

  const confirmNavigation = useCallback((action) => {
    if (anyDirty()) {
      pendingActionRef.current = action;
      setDialogOpen(true);
    } else {
      action();
    }
  }, [anyDirty]);

  useEffect(() => {
    const handler = (e) => {
      if (anyDirty()) {
        e.preventDefault();
        e.returnValue = '';
        return '';
      }
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [anyDirty]);

  const handleSave = async () => {
    setSaving(true);
    try {
      const dirtyEntries = [...registryRef.current.values()].filter((s) => s.isDirty);
      await Promise.all(dirtyEntries.map((s) => s.onSave?.()));
      setDialogOpen(false);
      const action = pendingActionRef.current;
      pendingActionRef.current = null;
      if (action) action();
    } finally {
      setSaving(false);
    }
  };

  const handleDiscard = () => {
    [...registryRef.current.values()].filter((s) => s.isDirty).forEach((s) => s.onDiscard?.());
    setDialogOpen(false);
    const action = pendingActionRef.current;
    pendingActionRef.current = null;
    if (action) action();
  };

  const handleCancel = () => {
    pendingActionRef.current = null;
    setDialogOpen(false);
  };

  return (
    <UnsavedChangesContext.Provider value={{ register, unregister, confirmNavigation }}>
      {children}
      <Dialog open={dialogOpen} onClose={handleCancel} maxWidth="xs" fullWidth>
        <DialogTitle>You have unsaved changes</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Save your changes before leaving, or discard them.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancel}>Cancel</Button>
          <Button onClick={handleDiscard} color="error">Discard</Button>
          <Button onClick={handleSave} variant="contained" disabled={saving}>Save</Button>
        </DialogActions>
      </Dialog>
    </UnsavedChangesContext.Provider>
  );
}

/**
 * Page-level hook: pass current dirty state + save/discard callbacks.
 * Returns confirmNavigation(action) for the page's own Cancel/back
 * buttons to route through (so a bare in-page Cancel gets the same
 * prompt as leaving the page entirely).
 */
export function useUnsavedChangesGuard(isDirty, { onSave, onDiscard } = {}) {
  const ctx = useContext(UnsavedChangesContext);
  if (!ctx) throw new Error('useUnsavedChangesGuard must be used within UnsavedChangesProvider');
  const { register, unregister, confirmNavigation } = ctx;
  const idRef = useRef(null);
  if (idRef.current === null) idRef.current = _nextRegistrationId++;

  useEffect(() => {
    register(idRef.current, { isDirty, onSave, onDiscard });
    return () => unregister(idRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isDirty, onSave, onDiscard]);

  return { confirmNavigation };
}

/** For components outside a guarded page (e.g. AppLayout's nav) that just
 * need to route navigation through whatever guard is currently active. */
export function useConfirmNavigation() {
  const ctx = useContext(UnsavedChangesContext);
  if (!ctx) throw new Error('useConfirmNavigation must be used within UnsavedChangesProvider');
  return ctx.confirmNavigation;
}
